import React, { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { urgentCaseAPI } from '../../services/api'
import SelectClinicModal from './SelectClinicModal'

const UrgentCaseNotificationModal = ({
  isOpen,
  onClose,
  notificationData,
  isLoading = false,
  onAcceptAndTreat
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [selectedMedia, setSelectedMedia] = useState(null)
  const [showMediaViewer, setShowMediaViewer] = useState(false)
  const [isAccepting, setIsAccepting] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)
  const [caseData, setCaseData] = useState(null)
  const [isLoadingCaseData, setIsLoadingCaseData] = useState(false)
  const [caseDataError, setCaseDataError] = useState(null)
  const hasPlayedSound = useRef(false)
  const lastNotificationId = useRef(null)

  // Parse notification data to find the actual data structure
  let actualNotificationData = notificationData
  if (notificationData?.data && typeof notificationData.data === 'string') {
    try {
      actualNotificationData = JSON.parse(notificationData.data)
    } catch (error) {
      console.error('❌ Error parsing nested data:', error)
    }
  }

  // Also parse the body field if it contains JSON (common in this notification structure)
  let bodyData = {}
  if (notificationData?.body && typeof notificationData.body === 'string') {
    try {
      bodyData = JSON.parse(notificationData.body)
    } catch (error) {
      console.error('❌ Error parsing body data:', error)
    }
  }

  // Function to load case data from API
  const loadCaseData = async () => {
    // Try to find content_id from various possible locations
    const contentId = notificationData?.content_id ||
      notificationData?.cid ||
      notificationData?.case_id ||
      actualNotificationData?.content_id ||
      actualNotificationData?.cid ||
      actualNotificationData?.case_id ||
      bodyData?.content_id ||
      bodyData?.cid ||
      bodyData?.case_id


    if (!contentId) {
      console.error('No content_id found in notification data')
      setCaseDataError(t("emergency.notification.noId"))
      return
    }

    setIsLoadingCaseData(true)
    setCaseDataError(null)

    try {
      const response = await urgentCaseAPI.getUrgentCase(contentId)

      // Extract the actual case data from the nested structure
      const caseDataFromAPI = response.data?.data
      if (caseDataFromAPI) {
        setCaseData(caseDataFromAPI)
      } else {
        throw new Error('Invalid response structure: missing data field')
      }
    } catch (error) {
      console.error('❌ Error loading case data:', error)
      setCaseDataError(error.message || t("emergency.notification.loadFailed"))
    } finally {
      setIsLoadingCaseData(false)
    }
  }

  // Function to play emergency alert sound
  const playAlertSound = () => {
    try {
      // Create audio context for emergency sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()

      // Function to create a single beep
      const createBeep = (frequency, duration, delay = 0) => {
        setTimeout(() => {
          const oscillator = audioContext.createOscillator()
          const gainNode = audioContext.createGain()

          oscillator.connect(gainNode)
          gainNode.connect(audioContext.destination)

          oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime)
          oscillator.type = 'sine'

          // Quick fade in/out for clean sound
          gainNode.gain.setValueAtTime(0, audioContext.currentTime)
          gainNode.gain.linearRampToValueAtTime(0.4, audioContext.currentTime + 0.01)
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration)

          oscillator.start(audioContext.currentTime)
          oscillator.stop(audioContext.currentTime + duration)
        }, delay)
      }

      // Emergency alert pattern: urgent beeping
      createBeep(900, 0.15, 0)     // High urgent beep
      createBeep(900, 0.15, 200)   // Repeat
      createBeep(700, 0.3, 500)    // Lower tone
      createBeep(900, 0.15, 900)   // High again
      createBeep(900, 0.15, 1100)  // Final beep

    } catch (error) {
      console.warn('Could not play emergency alert sound:', error)
    }
  }

  // Handle modal close - only allow closing through action buttons
  const handleClose = () => {
    if (!isLoading) {
      // Reset sound tracking when modal is closed
      hasPlayedSound.current = false
      onClose()
    }
  }

  // Handle backdrop click - disabled to prevent accidental dismissal
  const handleBackdropClick = (e) => {
    // Modal is not dismissable by clicking backdrop
    e.preventDefault()
    e.stopPropagation()
  }

  // Handle escape key - disabled to prevent accidental dismissal
  const handleKeyDown = (e) => {
    // Modal is not dismissable by escape key
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  // Handle view case action
  const handleViewCase = () => {
    if (notificationData?.action_url) {
      navigate(`/medical/${notificationData.action_url}`)
      handleClose()
    }
  }

  const [showClinicSelect, setShowClinicSelect] = useState(false)
  const [userClinics, setUserClinics] = useState([])
  const [pendingSenderId, setPendingSenderId] = useState(null)

  // Handle clinic selection
  const handleClinicSelect = (clinic) => {
    setShowClinicSelect(false)
    if (pendingSenderId && clinic) {
      navigate(`/medical/clinic/${clinic.id}/chat/${pendingSenderId}`)
      onClose()
    }
  }

  // Handle accept and treat action
  const handleAcceptAndTreat = async () => {
    setIsAccepting(true)
    try {
      // Use case ID from loaded case data, fallback to notification content_id
      const caseId = caseData?.id || notificationData?.content_id || actualNotificationData?.content_id || bodyData?.content_id
      if (caseId) {
        const token = localStorage.getItem('access_token')
        const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
        const response = await fetch(
          `${API_BASE_URL}/api/v1/pet-health/urgent-cases/${caseId}/accept`,
          {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            }
          }
        )

        // Only proceed if we get a successful response (status 200)
        if (response.status === 200) {
          const result = await response.json()

          // Store the case ID for later use in appointment modal
          const acceptedCaseId = caseId || result?.data?.case?.id || result?.data?.id
          if (acceptedCaseId) {
            localStorage.setItem('urgentCaseId', String(acceptedCaseId))
          }

          // Fetch user clinics to determine navigation
          const userDataStr = localStorage.getItem('user_data')
          let clinics = []

          if (userDataStr) {
            try {
              const userData = JSON.parse(userDataStr)
              const owned = userData.owned_clinics || []
              const member = userData.member_clinics || []
              // Combine and deduplicate
              const allClinics = [...owned, ...member].filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i)
              clinics = allClinics
              setUserClinics(clinics)
            } catch (e) {
              console.error("Error parsing user data for clinics", e)
            }
          }

          // Extract sender ID (pet owner's user ID) from the response
          // This is used for navigation to ensure we find the correct SOS conversation
          const conversationSenderId = result?.data?.conversation?.sender_id;

          // Store sender ID for navigation (prioritize conversation.sender_id over case senderId)
          const navigationId = conversationSenderId || senderId;
          setPendingSenderId(navigationId)

          if (clinics.length > 1) {
            // Show selection modal
            setShowClinicSelect(true)
          } else if (clinics.length === 1) {
            // Navigate directly using the sender ID
            navigate(`/medical/clinic/${clinics[0].id}/chat/${navigationId}`)
            onClose()
          } else {
            // Fallback if no clinics found
            navigate(`/medical/clinic/chat/${navigationId}`)
            onClose()
          }

        } else {
          // Parse the error response to get the specific error message
          let errorMessage = `Failed to accept S.O.S: ${response.status} - ${response.statusText}`
          try {
            const errorData = await response.json()

            if (errorData.message && errorData.message.includes('already been accepted')) {
              errorMessage = t("emergency.notification.alreadyAccepted") || 'This S.O.S has already been accepted by another doctor. The case is no longer available.'
              // Close the modal since the case is no longer available
              onClose()
            } else if (errorData.message) {
              errorMessage = errorData.message
            }
          } catch (parseError) {
            console.error('❌ Error parsing error response:', parseError)
          }

          throw new Error(errorMessage)
        }
      } else {
        throw new Error('No content_id found in notification data')
      }
    } catch (error) {
      console.error('❌ Error accepting urgent case:', error)
      // Don't proceed with opening telemedicine modal if API call fails
    } finally {
      setIsAccepting(false)
    }
  }

  // Handle dismiss action
  const handleDismiss = async () => {
    setIsRejecting(true)
    try {
      // Use case ID from loaded case data, fallback to notification content_id
      const caseId = caseData?.id || notificationData?.content_id || actualNotificationData?.content_id || bodyData?.content_id

      if (caseId) {
        const token = localStorage.getItem('access_token')
        const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

        const response = await fetch(
          `${API_BASE_URL}/api/v1/pet-health/urgent-cases/${caseId}/reject`,
          {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            }
          }
        )

        // Only proceed if we get a successful response (status 200)
        if (response.status === 200) {
          const result = await response.json()

          // Close the modal after successful rejection
          handleClose()
        } else {
          throw new Error(`Failed to reject urgent case: ${response.status} - ${response.statusText}`)
        }
      } else {
        throw new Error('No content_id found in notification data')
      }
    } catch (error) {
      console.error('❌ Error rejecting urgent case:', error)
      // Don't close modal if API call fails
      handleClose()
    } finally {
      setIsRejecting(false)
    }
  }

  // Handle media click
  const handleMediaClick = (mediaItem) => {
    setSelectedMedia(mediaItem)
    setShowMediaViewer(true)
  }

  // Handle media viewer close
  const handleMediaViewerClose = () => {
    setShowMediaViewer(false)
    setSelectedMedia(null)
  }

  useEffect(() => {
    // Note: No escape key listener added - modal is not dismissable by escape key
    document.body.style.overflow = 'hidden'

    // Load case data when modal opens
    if (isOpen && notificationData) {
      loadCaseData()

      const currentNotificationId = notificationData.case_id || notificationData.content_id || notificationData.cid || notificationData.id

      // Check if this is a new notification (different from the last one)
      if (currentNotificationId && currentNotificationId !== lastNotificationId.current) {
        playAlertSound()
        lastNotificationId.current = currentNotificationId
        hasPlayedSound.current = true
      } else if (!hasPlayedSound.current) {
        // Play sound for the first time this modal opens
        playAlertSound()
        hasPlayedSound.current = true
      } else {
      }
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, notificationData])

  // Reset sound tracking when component unmounts
  useEffect(() => {
    return () => {
      hasPlayedSound.current = false
      lastNotificationId.current = null
    }
  }, [])

  if (!isOpen || !notificationData) {
    return null;
  }

  // Don't render the modal content until case data is loaded (unless there's an error)
  if (isLoadingCaseData || (!caseData && !caseDataError)) {
    return createPortal(
      <>
        <style>{`
          @keyframes spinnerRotate {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .urgent-case-loading-spinner {
            width: 56px;
            height: 56px;
            position: relative;
            margin: 0 auto 16px auto;
            border: 3px solid rgba(0, 0, 0, 0.1);
            border-top-color: var(--user-type-color, #8548A8);
            border-radius: 50%;
            animation: spinnerRotate 1s linear infinite;
          }
          .urgent-case-loading-spinner::before {
            content: '';
            position: absolute;
            width: 56px;
            height: 56px;
            border-radius: 50%;
            border: 3px solid transparent;
            border-top-color: var(--user-type-color, #8548A8);
            border-right-color: var(--user-type-color, #8548A8);
            animation: spinnerRotate 1s cubic-bezier(0.5, 0, 0.5, 1) infinite;
          }
        `}</style>
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
        >
          <div
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '400px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              fontFamily: '"Red Hat Display", sans-serif'
            }}
          >
            <div className="urgent-case-loading-spinner"></div>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1F2937',
              margin: '0 0 8px 0',
              fontFamily: '"Red Hat Display", sans-serif'
            }}>
              {t("emergency.notification.loading")}
            </h3>
            <p style={{
              fontSize: '14px',
              color: '#6B7280',
              margin: 0,
              fontFamily: '"Red Hat Display", sans-serif'
            }}>
              {t("emergency.notification.fetching")}
            </p>
          </div>
        </div>
      </>,
      document.body
    )
  }

  // Show error state if case data failed to load
  if (caseDataError) {
    return createPortal(
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}
      >
        <div
          style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '400px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            fontFamily: '"Red Hat Display", sans-serif'
          }}
        >
          <div style={{
            width: '60px',
            height: '60px',
            backgroundColor: '#FEF2F2',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px auto',
            border: '2px solid #FECACA'
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1F2937',
            margin: '0 0 8px 0',
            fontFamily: '"Red Hat Display", sans-serif'
          }}>
            {t("emergency.notification.loadFailed")}
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#6B7280',
            margin: '0 0 16px 0',
            fontFamily: '"Red Hat Display", sans-serif'
          }}>
            {caseDataError}
          </p>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              backgroundColor: 'white',
              color: '#374151',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              fontFamily: '"Red Hat Display", sans-serif'
            }}
          >
            {t("emergency.notification.close")}
          </button>
        </div>
      </div>,
      document.body
    )
  }
  // Debug: Log the notification data to see what we're receiving


  // Use case data if available, otherwise fall back to notification data
  const dataSource = caseData || actualNotificationData;

  // Extract data from the case data or notification data
  let message = '', body = '', text = '', description = '', sender, location, distance_km, content_id, media = [], pet_data = [], uid, ct, click_action, notification_id, cid, title, user_name, latitude, longitude, action_url;

  try {
    ({
      message = '',
      body = '',
      text = '',
      description = '',
      sender,
      location,
      distance_km,
      content_id,
      media =[],
      pet_data =[],
      // Handle the actual fields present in the notification
      uid,
      ct,
      click_action,
      notification_id,
      cid,
      // Additional fields from the nested data
      title,
      user_name,
      latitude,
      longitude,
      action_url
    } = dataSource);
  } catch (error) {
    console.error('❌ Error extracting data from dataSource:', error);
  }


  // If we have case data, use it directly for better data structure
  if (caseData) {
    // Override with case data fields if they exist
    if (caseData.problem_description) message = caseData.problem_description
    if (caseData.media) media = caseData.media
    if (caseData.pets) pet_data = caseData.pets
    if (caseData.sender) sender = caseData.sender
    // Note: API response doesn't include location, distance_km
    // These would need to be handled separately or from notification data
  }

  // Extract data from body if it's parsed JSON
  const {
    message: bodyMessage = '',
    sender: bodySender,
    location: bodyLocation,
    distance_km: bodyDistance,
    content_id: bodyContentId,
    media: bodyMedia = [],
    pet_data: bodyPetData = []
  } = bodyData

  // Use the first available message field, prioritizing body message if available
  const notificationMessage = bodyMessage || message || body || text || description ||
    (ct === 'urgentmedicalcase' ? t("emergency.notification.nearbyDesc") : 'Urgent case notification') ||
    'No message available'


  // Use the best available sender data
  const senderName = bodySender?.name || sender?.full_name || sender?.name || user_name || 'Pet Guardian'
  const senderId = caseData?.sender?.id || bodySender?.id || sender?.id
  const senderImage = bodySender?.image || sender?.profile_image || sender?.image
  // const distance = (bodyDistance || distance_km) ? `${(bodyDistance || distance_km).toFixed(1)} km away` : 'Location unknown'




  // Get pet information from pet_data array (preferred) or fallback to message extraction
  const finalPetData = bodyPetData.length > 0 ? bodyPetData : pet_data
  const primaryPet = finalPetData && finalPetData.length > 0 ? finalPetData[0] : null




  // Check if this case has been accepted by another doctor
  const isAcceptedByOther = actualNotificationData.accepted_by_other ||
    actualNotificationData.case_status === 'accepted' ||
    actualNotificationData.accepted_by_doctor_id
  const acceptedByDoctorName = actualNotificationData.accepted_by_doctor_name
  const acceptedAt = actualNotificationData.accepted_at

  // Check if this is the accepted modal for Pet Guardians
  const isAcceptedModal = actualNotificationData.is_accepted_modal

  const petName = primaryPet?.name || (() => {
    if (notificationMessage && typeof notificationMessage === 'string') {
      const petNameMatch = notificationMessage.match(/for ([^:]+):/)
      return petNameMatch ? petNameMatch[1] : 'Unknown Pet'
    }
    return 'Unknown Pet'
  })()


  const modalContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
    >
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          padding: '32px',
          maxWidth: '700px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          transform: isOpen ? 'scale(1)' : 'scale(0.95)',
          transition: 'transform 0.2s ease-out, box-shadow 0.3s ease-out',
          fontFamily: '"Red Hat Display", sans-serif',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with Emergency Icon */}
        <div style={{ marginBottom: '24px', textAlign: 'center' }}>
          {isAcceptedModal ? (
            // Accepted Modal Header
            <>
              <div style={{
                width: '80px',
                height: '80px',
                backgroundColor: '#F0FDF4',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px auto',
                border: '3px solid #10B981'
              }}>
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#10B981"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: '700',
                color: '#1F2937',
                margin: '0 0 8px 0',
                fontFamily: '"Red Hat Display", sans-serif'
              }}>
                {t("emergency.notification.updateTitle")}
              </h2>
              <p style={{
                fontSize: '18px',
                color: '#10B981',
                margin: '0 0 16px 0',
                fontWeight: '600',
                fontFamily: '"Red Hat Display", sans-serif'
              }}>
                {t("emergency.notification.acceptedTitle")}
              </p>
              <p style={{
                fontSize: '16px',
                color: '#6B7280',
                margin: '0 0 24px 0',
                lineHeight: '1.5',
                fontFamily: '"Red Hat Display", sans-serif'
              }}>
                {t("emergency.notification.acceptedDesc")}
              </p>
            </>
          ) : (
            // Regular Emergency Header
            <>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#EF4444',
                margin: '0 0 8px 0',
                fontFamily: '"Red Hat Display", sans-serif'
              }}>
                {t("emergency.notification.urgentTitle")}
              </h2>
              <p style={{
                fontSize: '14px',
                color: '#6B7280',
                margin: 0,
                fontFamily: '"Red Hat Display", sans-serif'
              }}>
                {t("emergency.notification.nearbyDesc")}
              </p>
            </>
          )}
        </div>

        {/* Case Information */}
        <div style={{ marginBottom: '24px' }}>
          {isAcceptedModal ? (
            // Accepted Modal Case Details
            <div style={{
              backgroundColor: '#F0F8FF',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #B3D9FF',
              marginBottom: '24px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1F2937',
                margin: '0 0 16px 0',
                fontFamily: '"Red Hat Display", sans-serif'
              }}>
                {t("emergency.notification.caseDetails")}
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '12px'
              }}>
                <div>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#6B7280',
                    fontFamily: '"Red Hat Display", sans-serif'
                  }}>
                    {t("emergency.notification.caseId").split(':')[0]}:
                  </span>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1F2937',
                    fontFamily: '"Red Hat Display", sans-serif'
                  }}>
                    {t("emergency.notification.caseId", { id: actualNotificationData.case_id || 'N/A' })}
                  </div>
                </div>
                <div>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#6B7280',
                    fontFamily: '"Red Hat Display", sans-serif'
                  }}>
                    {t("emergency.notification.doctor")}:
                  </span>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1F2937',
                    fontFamily: '"Red Hat Display", sans-serif'
                  }}>
                    {acceptedByDoctorName || t("emergency.roleVeterinarian")}
                  </div>
                </div>
                <div>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#6B7280',
                    fontFamily: '"Red Hat Display", sans-serif'
                  }}>
                    {t("emergency.notification.pet")}:
                  </span>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1F2937',
                    fontFamily: '"Red Hat Display", sans-serif'
                  }}>
                    {petName}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Regular Case Information
            <>
              {/* Sender Information */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#F9FAFB',
                borderRadius: '8px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#E5E7EB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}>
                  {senderImage ? (
                    <img
                      src={senderImage}
                      alt={senderName}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#6B7280"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  )}
                </div>
                <div>
                  <p style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#1F2937',
                    margin: '0 0 2px 0',
                    fontFamily: '"Red Hat Display", sans-serif'
                  }}>
                    {senderName}
                  </p>
                  <p style={{
                    fontSize: '12px',
                    color: '#6B7280',
                    margin: 0,
                    fontFamily: '"Red Hat Display", sans-serif'
                  }}>
                    {t("emergency.notification.senderRole")}
                  </p>
                </div>
              </div>

              {/* Pet Information */}
              <div style={{
                backgroundColor: '#F0F9FF',
                border: '1px solid #BAE6FD',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px'
                }}>
                  {/* Pet Image */}
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    backgroundColor: '#E5E7EB',
                    flexShrink: 0
                  }}>
                    {primaryPet?.image ? (
                      <img
                        src={primaryPet.image}
                        alt={petName}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#0EA5E9'
                      }}>
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Pet Details */}
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#0C4A6E',
                        margin: 0,
                        fontFamily: '"Red Hat Display", sans-serif'
                      }}>
                        {petName}
                      </h3>
                      {primaryPet?.gender && (
                        <span style={{
                          fontSize: '12px',
                          backgroundColor: '#0EA5E9',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontWeight: '500',
                          fontFamily: '"Red Hat Display", sans-serif'
                        }}>
                          {primaryPet.gender === 'male' ? '♂' : '♀'} {primaryPet.gender}
                        </span>
                      )}
                      {primaryPet?.age && primaryPet.age !== '' && (
                        <span style={{
                          fontSize: '12px',
                          backgroundColor: '#10B981',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontWeight: '500',
                          fontFamily: '"Red Hat Display", sans-serif'
                        }}>
                          {t("emergency.notification.age")}: {primaryPet.age}
                        </span>
                      )}
                    </div>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                      gap: '8px',
                      fontSize: '12px',
                      color: '#0369A1',
                      fontFamily: '"Red Hat Display", sans-serif'
                    }}>
                      {primaryPet?.species && (
                        <div>
                          <span style={{ fontWeight: '500' }}>{t("emergency.notification.species")}:</span> {typeof primaryPet.species === 'object' ? (primaryPet.species.name || primaryPet.species.display_name || t("emergency.notification.unknown")) : primaryPet.species}
                        </div>
                      )}
                      {primaryPet?.breed && (
                        <div>
                          <span style={{ fontWeight: '500' }}>{t("emergency.notification.breed")}:</span> {typeof primaryPet.breed === 'object' ? (primaryPet.breed.name || primaryPet.breed.display_name || t("emergency.notification.unknown")) : primaryPet.breed}
                        </div>
                      )}
                    </div>

                    <div style={{
                      marginTop: '8px',
                      fontSize: '12px',
                      color: '#EF4444',
                      fontWeight: '500',
                      fontFamily: '"Red Hat Display", sans-serif'
                    }}>
                      {t("emergency.notification.petInEmergency")}
                    </div>
                  </div>
                </div>

                {/* Multiple Pets Indicator */}
                {pet_data && pet_data.length > 1 && (
                  <div style={{
                    marginTop: '12px',
                    padding: '8px 12px',
                    backgroundColor: '#E0F2FE',
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: '#0369A1',
                    fontFamily: '"Red Hat Display", sans-serif'
                  }}>
                    {t("emergency.notification.morePets", { count: pet_data.length - 1 })}
                  </div>
                )}
              </div>

              {/* Emergency Description */}
              <div style={{
                backgroundColor: '#FEF2F2',
                border: '1px solid #FECACA',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px'
                }}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#EF4444"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#EF4444',
                    fontFamily: '"Red Hat Display", sans-serif'
                  }}>
                    {t("emergency.notification.emergencyDesc")}
                  </span>
                </div>
                <p style={{
                  fontSize: '16px',
                  fontWeight: '500',
                  color: '#1F2937',
                  margin: 0,
                  fontFamily: '"Red Hat Display", sans-serif',
                  lineHeight: '1.5'
                }}>
                  {notificationMessage}
                </p>
              </div>

              {/* Media Section */}
              {media && media.length > 0 && (
                <div style={{
                  backgroundColor: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px'
                  }}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#6B7280"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21,15 16,10 5,21" />
                    </svg>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151',
                      fontFamily: '"Red Hat Display", sans-serif'
                    }}>
                      {t("emergency.notification.emergencyMedia", { count: media.length })}
                    </span>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: media.length === 1
                      ? 'repeat(auto-fit, minmax(120px, max-content))'
                      : 'repeat(auto-fit, minmax(80px, 1fr))',
                    gap: '8px',
                    justifyContent: media.length === 1 ? 'flex-start' : 'stretch'
                  }}>
                    {media.map((mediaItem, index) => (
                      <div
                        key={mediaItem.id || index}
                        onClick={() => handleMediaClick(mediaItem)}
                        style={{
                          height: '120px',
                          position: 'relative',
                          aspectRatio: '1',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          backgroundColor: '#E5E7EB',
                          border: '2px solid transparent',
                          transition: 'border-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.borderColor = '#EF4444'
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.borderColor = 'transparent'
                        }}
                      >
                        {mediaItem.is_image ? (
                          <img
                            src={mediaItem.url}
                            alt={mediaItem.file_name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#1F2937',
                            color: 'white'
                          }}>
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polygon points="23 7 16 12 23 17 23 7" />
                              <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                            </svg>
                          </div>
                        )}

                        {/* Media type indicator */}
                        <div style={{
                          position: 'absolute',
                          top: '4px',
                          right: '4px',
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          color: 'white',
                          borderRadius: '4px',
                          padding: '2px 6px',
                          fontSize: '10px',
                          fontWeight: '500',
                          fontFamily: '"Red Hat Display", sans-serif'
                        }}>
                          {mediaItem.is_image ? 'IMG' : 'VID'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Action Buttons */}
        {isAcceptedModal ? (
          // Accepted Modal Action Buttons
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            alignItems: 'center'
          }}>
            <button
              type="button"
              onClick={() => {
                // Get conversation ID from caseData (preferred) or notification data
                const conversationId = caseData?.conversation?.id ||
                  actualNotificationData.conversation_id ||
                  notificationData.conversation_id ||
                  caseData?.conversation_id;

                // Fallback to doctor ID if conversation not found (though backend guarantees conversation)
                const doctorId = caseData?.doctor?.id ||
                  actualNotificationData.doctor_id ||
                  notificationData.doctor_id;

                if (conversationId) {
                  navigate(`/chat/${conversationId}/medical`);
                  onClose();
                } else if (doctorId) {
                  navigate(`/chat/${doctorId}/medical`);
                  onClose();
                } else {
                  console.error('No conversation id or doctor id found for chat navigation');
                }
              }}
              style={{
                padding: '16px 32px',
                border: 'none',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                fontFamily: '"Red Hat Display", sans-serif',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 8px 20px -5px rgba(16, 185, 129, 0.3), 0 4px 6px -2px rgba(16, 185, 129, 0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)'
                e.target.style.boxShadow = '0 20px 40px -10px rgba(16, 185, 129, 0.4), 0 8px 16px -4px rgba(16, 185, 129, 0.2)'
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)'
                e.target.style.boxShadow = '0 8px 20px -5px rgba(16, 185, 129, 0.3), 0 4px 6px -2px rgba(16, 185, 129, 0.1)'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              {t("emergency.notification.goToChat")}
            </button>
            <p style={{
              fontSize: '14px',
              color: '#6B7280',
              margin: 0,
              textAlign: 'center',
              fontFamily: '"Red Hat Display", sans-serif'
            }}>
              {t("emergency.notification.redirectNote")}
            </p>
          </div>
        ) : isAcceptedByOther ? (
          // Show accepted by other doctor message
          <div style={{
            padding: '16px',
            backgroundColor: '#FEF3C7',
            border: '1px solid #F59E0B',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '8px'
            }}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#F59E0B"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span style={{
                color: '#92400E',
                fontWeight: '600',
                fontSize: '16px'
              }}>
                {t("emergency.notification.alreadyAcceptedTitle")}
              </span>
            </div>



            <button
              type="button"
              onClick={onClose}
              style={{
                marginTop: '12px',
                padding: '8px 16px',
                border: '1px solid #F59E0B',
                borderRadius: '6px',
                backgroundColor: 'white',
                color: '#92400E',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                fontFamily: '"Red Hat Display", sans-serif',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#FEF3C7'
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'white'
              }}
            >
              {t("emergency.notification.close")}
            </button>
          </div>
        ) : (
          // Show normal action buttons
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'space-between'
          }}>
            <button
              type="button"
              onClick={handleDismiss}
              disabled={isLoading || isRejecting}
              style={{
                flex: 1,
                padding: '12px 24px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                backgroundColor: isRejecting ? '#F3F4F6' : 'white',
                color: isRejecting ? '#9CA3AF' : '#374151',
                fontSize: '14px',
                fontWeight: '500',
                cursor: (isLoading || isRejecting) ? 'not-allowed' : 'pointer',
                fontFamily: '"Red Hat Display", sans-serif',
                opacity: (isLoading || isRejecting) ? 0.6 : 1,
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (!isLoading && !isRejecting) {
                  e.target.style.backgroundColor = '#F9FAFB'
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading && !isRejecting) {
                  e.target.style.backgroundColor = 'white'
                }
              }}
            >
              {isRejecting && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                  <line x1="12" y1="2" x2="12" y2="6" />
                  <line x1="12" y1="18" x2="12" y2="22" />
                  <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
                  <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
                  <line x1="2" y1="12" x2="6" y2="12" />
                  <line x1="18" y1="12" x2="22" y2="12" />
                  <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
                  <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
                </svg>
              )}
              {isRejecting ? t("emergency.notification.rejecting") : t("emergency.notification.reject")}
            </button>
            <button
              type="button"
              onClick={handleAcceptAndTreat}
              disabled={isLoading || isAccepting}
              style={{
                flex: 2,
                padding: '12px 24px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: (isLoading || isAccepting) ? '#D1D5DB' : '#00D4B3',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: (isLoading || isAccepting) ? 'not-allowed' : 'pointer',
                fontFamily: '"Red Hat Display", sans-serif',
                transition: 'all 0.2s ease',
                opacity: (isLoading || isAccepting) ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (!isLoading && !isAccepting) {
                  e.target.style.backgroundColor = '#00BFA5'
                  e.target.style.opacity = '0.9'
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading && !isAccepting) {
                  e.target.style.backgroundColor = '#00D4B3'
                  e.target.style.opacity = '1'
                }
              }}
            >
              {(isLoading || isAccepting) && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                  <line x1="12" y1="2" x2="12" y2="6" />
                  <line x1="12" y1="18" x2="12" y2="22" />
                  <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
                  <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
                  <line x1="2" y1="12" x2="6" y2="12" />
                  <line x1="18" y1="12" x2="22" y2="12" />
                  <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
                  <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
                </svg>
              )}
              {isAccepting ? t("emergency.notification.accepting") : (isLoading ? t("emergency.notification.actionLoading") : t("emergency.notification.acceptCase"))}
            </button>
          </div>
        )}
      </div>
    </div>
  )

  return createPortal(
    <>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      {modalContent}
      {/* Media Viewer Modal */}
      {showMediaViewer && selectedMedia && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '20px'
          }}
          onClick={handleMediaViewerClose}
        >
          <div
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              backgroundColor: 'white',
              borderRadius: '12px',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={handleMediaViewerClose}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                zIndex: 10
              }}
            >
              ×
            </button>

            {/* Media content */}
            {selectedMedia.is_image ? (
              <img
                src={selectedMedia.url}
                alt={selectedMedia.file_name}
                style={{
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            ) : (
              <video
                src={selectedMedia.url}
                controls
                style={{
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  display: 'block'
                }}
              />
            )}

            {/* Media info */}
            <div style={{
              position: 'absolute',
              bottom: '12px',
              left: '12px',
              right: '12px',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontFamily: '"Red Hat Display", sans-serif'
            }}>
              <div style={{ fontWeight: '500', marginBottom: '2px' }}>
                {selectedMedia.file_name}
              </div>
              <div style={{ opacity: 0.8 }}>
                {selectedMedia.formatted_size} • {selectedMedia.is_image ? t('postFeed.image') : t('postFeed.video')}
              </div>
            </div>
          </div>
        </div>
      )}
      <SelectClinicModal
        open={showClinicSelect}
        clinics={userClinics}
        defaultRoute={`chat/${pendingSenderId}`}
        onSelect={() => {
          setShowClinicSelect(false)
          onClose()
        }}
      />
    </>,
    document.body
  )
}

export default UrgentCaseNotificationModal
